import { supabase } from "../supabase/supabaseClient";
import { followUser } from "./followsService";

/**
 * Send a connection request to another user
 */
export const sendConnectionRequest = async (addresseeId) => {
    try {
        const { data: sessionData } = await supabase.auth.getSession();
        const requesterId = sessionData?.session?.user?.id;

        if (!requesterId) {
            throw new Error("User not authenticated");
        }

        const { data, error } = await supabase
            .from("user_connections")
            .insert([
                {
                    requester_id: requesterId,
                    addressee_id: addresseeId,
                    status: "pending"
                }
            ])
            .select();

        if (error) throw error;
        return { success: true, data };
    } catch (error) {
        console.error("Error sending connection request:", error);
        throw error;
    }
};

/**
 * Get pending connection requests for the current user (where they are the addressee)
 */
export const getPendingRequests = async () => {
    try {
        const { data: sessionData } = await supabase.auth.getSession();
        const userId = sessionData?.session?.user?.id;

        if (!userId) {
            throw new Error("User not authenticated");
        }

        const { data, error } = await supabase
            .from("user_connections")
            .select(`
                id,
                requester_id,
                addressee_id,
                status,
                created_at,
                updated_at,
                user_profiles!requester_id(first_name, last_name, bio, city, role)
            `)
            .eq("addressee_id", userId)
            .eq("status", "pending");

        if (error) throw error;
        return data || [];
    } catch (error) {
        console.error("Error fetching pending requests:", error);
        throw error;
    }
};

/**
 * Get accepted connections for the current user
 */
export const getAcceptedConnections = async () => {
    try {
        const { data: sessionData } = await supabase.auth.getSession();
        const userId = sessionData?.session?.user?.id;

        if (!userId) {
            throw new Error("User not authenticated");
        }

        const { data, error } = await supabase
            .from("user_connections")
            .select(`
                id,
                requester_id,
                addressee_id,
                status,
                created_at,
                updated_at
            `)
            .eq("status", "accepted")
            .or(`requester_id.eq.${userId},addressee_id.eq.${userId}`);

        if (error) throw error;
        return data || [];
    } catch (error) {
        console.error("Error fetching accepted connections:", error);
        throw error;
    }
};

/**
 * Get all connection relationships for the current user
 */
export const getConnectionsForCurrentUser = async () => {
    try {
        const { data: sessionData } = await supabase.auth.getSession();
        const userId = sessionData?.session?.user?.id;

        if (!userId) {
            throw new Error("User not authenticated");
        }

        const { data, error } = await supabase
            .from("user_connections")
            .select("id, requester_id, addressee_id, status, created_at, updated_at")
            .or(`requester_id.eq.${userId},addressee_id.eq.${userId}`)
            .order("updated_at", { ascending: false });

        if (error) throw error;
        return data || [];
    } catch (error) {
        console.error("Error fetching connections for current user:", error);
        throw error;
    }
};

/**
 * Accept a connection request
 */
export const acceptConnectionRequest = async (connectionId) => {
    try {
        const { data, error } = await supabase
            .from("user_connections")
            .update({ status: "accepted", updated_at: new Date().toISOString() })
            .eq("id", connectionId)
            .select();

        if (error) throw error;

        const acceptedConnection = Array.isArray(data) ? data[0] : null;
        if (acceptedConnection?.requester_id) {
            const followResult = await followUser(acceptedConnection.requester_id);
            if (!followResult?.success && followResult?.error) {
                console.error("Connection accepted but follow sync failed:", followResult.error);
            }
        }

        return { success: true, data };
    } catch (error) {
        console.error("Error accepting connection request:", error);
        throw error;
    }
};

/**
 * Reject a connection request
 */
export const rejectConnectionRequest = async (connectionId) => {
    try {
        const { data, error } = await supabase
            .from("user_connections")
            .update({ status: "rejected", updated_at: new Date().toISOString() })
            .eq("id", connectionId)
            .select();

        if (error) throw error;
        return { success: true, data };
    } catch (error) {
        console.error("Error rejecting connection request:", error);
        throw error;
    }
};

/**
 * Get the connection status between two users
 */
export const getConnectionStatus = async (otherUserId) => {
    try {
        const { data: sessionData } = await supabase.auth.getSession();
        const currentUserId = sessionData?.session?.user?.id;

        if (!currentUserId) {
            throw new Error("User not authenticated");
        }

        const { data, error } = await supabase
            .from("user_connections")
            .select("status")
            .or(
                `and(requester_id.eq.${currentUserId},addressee_id.eq.${otherUserId}),and(requester_id.eq.${otherUserId},addressee_id.eq.${currentUserId})`
            )
            .limit(1)
            .single();

        if (error && error.code !== "PGRST116") {
            // PGRST116 means no rows found, which is fine
            throw error;
        }

        return data?.status || null;
    } catch (error) {
        console.error("Error fetching connection status:", error);
        return null;
    }
};

/**
 * Get connection relationship details between current user and another user
 */
export const getConnectionRelationship = async (otherUserId) => {
    try {
        const { data: sessionData } = await supabase.auth.getSession();
        const currentUserId = sessionData?.session?.user?.id;

        if (!currentUserId) {
            throw new Error("User not authenticated");
        }

        const { data, error } = await supabase
            .from("user_connections")
            .select("id, status, requester_id, addressee_id")
            .or(
                `and(requester_id.eq.${currentUserId},addressee_id.eq.${otherUserId}),and(requester_id.eq.${otherUserId},addressee_id.eq.${currentUserId})`
            )
            .limit(1)
            .single();

        if (error && error.code !== "PGRST116") {
            throw error;
        }

        return data || null;
    } catch (error) {
        console.error("Error fetching connection relationship:", error);
        return null;
    }
};

/**
 * Get accepted connection user profiles for the current user
 */
export const getAcceptedConnectionProfiles = async () => {
    try {
        const { data: sessionData } = await supabase.auth.getSession();
        const currentUserId = sessionData?.session?.user?.id;

        if (!currentUserId) {
            throw new Error("User not authenticated");
        }

        const { data: connections, error: connError } = await supabase
            .from("user_connections")
            .select("requester_id, addressee_id")
            .eq("status", "accepted")
            .or(`requester_id.eq.${currentUserId},addressee_id.eq.${currentUserId}`);

        if (connError) throw connError;

        const otherUserIds = Array.from(
            new Set(
                (connections || []).map((connection) =>
                    connection.requester_id === currentUserId
                        ? connection.addressee_id
                        : connection.requester_id
                )
            )
        );

        if (!otherUserIds.length) {
            return [];
        }

        const { data: profiles, error: profileError } = await supabase
            .from("user_profiles")
            .select("user_id, first_name, last_name, city, role, avatar_url")
            .in("user_id", otherUserIds);

        if (profileError) throw profileError;

        const { data: playerProfiles, error: playerProfilesError } = await supabase
            .from("player_profiles")
            .select("id, user_id, is_public, firstname, lastname, city, avatar_url, positions")
            .in("user_id", otherUserIds);

        if (playerProfilesError) throw playerProfilesError;

        const profileMap = new Map((profiles || []).map((profile) => [profile.user_id, profile]));
        const playerProfileMap = new Map((playerProfiles || []).map((profile) => [profile.user_id, profile]));

        return otherUserIds.map((userId) => {
            const profile = profileMap.get(userId);
            const playerProfile = playerProfileMap.get(userId);
            const derivedRole = (profile?.role || "").toString().trim() || (playerProfile?.id ? "player" : "member");
            return {
                user_id: userId,
                first_name: profile?.first_name || playerProfile?.firstname || "",
                last_name: profile?.last_name || playerProfile?.lastname || "",
                city: profile?.city || playerProfile?.city || "",
                role: derivedRole,
                primary_position: Array.isArray(playerProfile?.positions) ? (playerProfile.positions[0] || "") : "",
                avatar_url: profile?.avatar_url || playerProfile?.avatar_url || null,
                player_profile_id: playerProfile?.id || null,
                has_public_player_profile: Boolean(playerProfile?.id && playerProfile?.is_public),
            };
        });
    } catch (error) {
        console.error("Error fetching accepted connection profiles:", error);
        throw error;
    }
};
